import React from 'react';
import { computed, reaction, action } from 'mobx';

import { Variables } from 'core/query';
import { clientStore } from 'apps/client/app';

import { PaginationOffsetManager } from 'libs/connection';
import { ShipmentQuoteModel } from 'apps/shipmentQuote/app';
import {
  ShipmentQuotesConnectionQuery,
  IntermediatorShipmentQuotesConnectionQuery,
} from 'apps/shipmentQuote/gql';
import {
  RfqNameLink,
  CadPreview,
  makeDecimalIdLink,
  Space,
} from 'libs/connection/cells';
import { ToggleButton } from 'libs/connection/Table/ToggleButton';
import { SpaceFilter, DateFilter } from 'libs/connection/actions/filters';
// import { DateCell } from 'libs/connection/cells';
import { ValueInputType } from 'libs/connection/actions/filterModels';

import { Paragraph } from 'components';

import { graphqlIdToDecimal } from 'utils/dgraphId';
import { PortalLink } from 'apps/client/components/PortalLink';
import {
  ProductQuotePreviewCell,
  ProductQuoteBuildDetailsCell,
} from 'apps/productQuote/cells';
import { formatPrice, formatDate } from 'utils/formatStrings';
import { formatDistance, parseISO } from 'date-fns';

const customerSpaceCol = {
  Header: 'Customer',
  accessor: 'customerSpace',
  Cell: Space,
  filter: SpaceFilter,
  fieldType: ValueInputType.STRING,
  filterParamName: 'customerSpaceID',
};

// TODO (david): refactor this into its own file, deduplicate and reuse code.
export const columns = [
  {
    Header: 'Expires',
    accessor: 'validUntil',
    Cell: function ValidUntil(validUntil) {
      const validUntilDate = parseISO(validUntil);
      if (validUntilDate) {
        const expiresInTheFuture = validUntilDate > new Date();
        const text = formatDistance(validUntilDate, new Date(), {
          addSuffix: true,
        });
        return <Paragraph>{expiresInTheFuture ? text : ' Expired'}</Paragraph>;
      }
      return <Paragraph>-</Paragraph>;
    },
    SubCell: function ValidUntilSubCell(productQuote) {
      if (productQuote.validUntil) {
        return <Paragraph>{formatDate(productQuote.validUntil)}</Paragraph>;
      }
      return <Paragraph>-</Paragraph>;
    },
  },
  {
    Header: 'Preview',
    accessor: '',
    Cell: function Preview(quote, column, conn, rowState) {
      if (quote.productQuotes.length === 1) {
        const { file } = quote.productQuotes[0].for.build;
        // TODO (david): these 42px styles are a quick fix so that the image does not
        // stretch the row vertically more than the base 60px. Should implement reusable wrapper
        // or pass better props to preview. Also suspect he preview has a huge resolution for
        // this usecase
        return (
          <PortalLink
            name='quote'
            params={{ quoteId: graphqlIdToDecimal(quote.id) }}
            // onClick={onOrderCellClicked}
          >
            <div style={{ height: '42px' }}>{CadPreview(file)}</div>
          </PortalLink>
        );
      }

      return (
        <ToggleButton
          onClick={rowState.onToggleExpand}
          isToggled={rowState.isExpanded}
        />
      );
      // return <Paragraph>[Multipart]</Paragraph>;
    },
    SubCell: ProductQuotePreviewCell,
  },
  {
    Header: 'Quote Id',
    accessor: '',
    Cell: function quoteLink(quote) {
      return makeDecimalIdLink({
        routeName: 'quote',
        paramName: 'quoteId',
        prefix: 'Q',
      })(quote.id);
    },
  },
  {
    Header: 'RFQ ID',
    Cell: function RfqLink(quote) {
      if (quote.productQuotes.length > 1) {
        return <Paragraph>...</Paragraph>;
      }
      if (quote.productQuotes.length === 1) {
        return <RfqNameLink rfq={quote.productQuotes[0].from} showId />;
      }
      return <Paragraph>-</Paragraph>;
    },
    SubCell: function RfqLinkSubCell(productQuote) {
      return <RfqNameLink rfq={productQuote.from} showId />;
    },
  },
  {
    Header: 'Part name',
    accessor: '',
    Cell: function PartName(quote) {
      if (quote.productQuotes.length === 1) {
        return (
          <Paragraph>
            {quote.productQuotes[0].for.build.file.filename}
          </Paragraph>
        );
      }
      return <Paragraph>...</Paragraph>;
    },
    SubCell: function PartNameSubCell(productQuote) {
      return <Paragraph>{productQuote.for.build.file.filename}</Paragraph>;
    },
  },
  {
    Header: 'Part Details',
    accessor: '',
    Cell: function PartDetails(quote) {
      if (quote.productQuotes.length === 1) {
        return ProductQuoteBuildDetailsCell(quote.productQuotes[0]);
      }
      return <Paragraph>...</Paragraph>;
    },
    SubCell: ProductQuoteBuildDetailsCell,
  },
  {
    Header: 'Quantity',
    accessor: '',
    Cell: function Quantity(quote) {
      const quantities = quote.productQuotes
        .map(productQuote => productQuote.for.quantity)
        .join(', ');
      return <Paragraph>{quantities}</Paragraph>;
    },
    SubCell: function QuantitySubCell(productQuote) {
      return <Paragraph>{productQuote.for.quantity}</Paragraph>;
    },
  },
  {
    Header: 'Unit Price',
    accessor: '',
    Cell: function UnitPrice(quote) {
      if (quote.productQuotes.length === 1) {
        const { finalUnitCost, currency } = quote.productQuotes[0];
        return (
          <Paragraph>{formatPrice(finalUnitCost / 100, currency)}</Paragraph>
        );
      }
      return <Paragraph>.</Paragraph>;
    },
    SubCell: function UnitPriceSubCell(productQuote) {
      const { finalUnitCost, currency } = productQuote;
      return (
        <Paragraph>{formatPrice(finalUnitCost / 100, currency)}</Paragraph>
      );
    },
  },
  {
    Header: 'Total Price',
    accessor: '',
    Cell: function TotalCost(quote) {
      const { currency } = quote.productQuotes[0];
      return (
        <Paragraph>{formatPrice(quote.totalCost / 100, currency)}</Paragraph>
      );
    },
    SubCell: function TotalCostSubCell(productQuote) {
      const { cost, currency } = productQuote;
      return <Paragraph>{formatPrice(cost / 100, currency)}</Paragraph>;
    },
  },
  {
    Header: 'Created Date',
    isSortable: true,
    filter: DateFilter,
    accessor: 'createdAt',
    fieldType: ValueInputType.DATETIME,
    Cell: function CreatedDate(date) {
      return <Paragraph>{formatDate(date)}</Paragraph>;
    },
    SubCell: function CreatedDateSubCell(productQuote) {
      return <Paragraph>{formatDate(productQuote.createdAt)}</Paragraph>;
    },
  },
];
export class ShipmentQuotesConnectionManager extends PaginationOffsetManager {
  static ElementModel = ShipmentQuoteModel;

  static defaultOrder = 'DESC';
  static defaultSort = 'createdAt';

  constructor(parent, init, options) {
    super(parent, init, options);
    this.batchSize = 10;

    this.variables = this.variables.extend(
      new Variables({
        spaceId: () =>
          clientStore.activeSpaceId || clientStore.signedInProfile.inSpace.id,
      }),
    );

    this.variables.onChange(
      ['spaceId'],
      () => {
        this.initiateQuery();
      },
      { fireImmediately: false },
    );

    this.disposers.push(
      reaction(
        () => clientStore.currentPortal,
        () => {
          this.initiateQuery();
        },
        { fireImmediately: true },
      ),
    );
  }

  @computed get finalColumns() {
    if (clientStore.currentPortal === 'INTERMEDIATOR') {
      const cols = [...columns];
      cols.splice(3, 0, customerSpaceCol);

      return cols;
    }
    return columns;
  }

  @computed get filterColumns() {
    return this.finalColumns.filter(col => col.filter !== undefined);
  }

  @action initiateQuery = () => {
    this.InitialiseFilters();
    const shouldQueryAsIntermediator =
      clientStore.currentPortal === 'INTERMEDIATOR';

    const Query = shouldQueryAsIntermediator
      ? IntermediatorShipmentQuotesConnectionQuery
      : ShipmentQuotesConnectionQuery;
    this.initialiseQuery(Query, this.variables, clientStore.roleHeaders);
  };
}
